// src/api/httpClient.ts
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';

// Create axios instance
const httpClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach token and group information
httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get API token from auth store
      const { apiToken } = useAuthStore.getState();
      if (apiToken && config.headers) {
        config.headers.Authorization = `Bearer ${apiToken}`;
      }
      
      // Add selected group from localStorage unless the request sets X-Selected-Group explicitly (e.g. admin scoped fetch).
      if (config.headers) {
        const hasExplicitGroup =
          config.headers['X-Selected-Group'] != null && config.headers['X-Selected-Group'] !== '';
        if (!hasExplicitGroup) {
          const selectedGroup = localStorage.getItem('selectedGroup');
          if (selectedGroup) {
            config.headers['X-Selected-Group'] = selectedGroup;
          }
        }
      }
      
      return config;
    } catch (error) {
      // If token acquisition fails, proceed without token
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    // Check if error is due to unauthorized access
    if (error.response && error.response.status === 401) {
      // Only clear auth state if we get a specific error code
      const errorCode = error.response.data?.error?.code;
      if (errorCode === 'invalid_token' || errorCode === 'token_expired') {
        // Clear auth state
        useAuthStore.getState().clearAuth();
        // Redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Utility function to create API request config with group-specific parameters
 * @param groupId - The group ID to include in the request 
 * @param config - Optional additional axios config
 * @returns AxiosRequestConfig with group parameters added
 */
export const withGroup = (groupId: string | null, config: AxiosRequestConfig = {}): AxiosRequestConfig => {
  // If no group ID is provided, use the one from localStorage
  const selectedGroup = groupId || localStorage.getItem('selectedGroup');
  
  if (!selectedGroup) {
    return config;
  }
  
  // Add group to URL params
  const params = { 
    ...(config.params || {}),
    groupId: selectedGroup 
  };
  
  // Add group to headers
  const headers = { 
    ...(config.headers || {}),
    'X-Selected-Group': selectedGroup 
  };
  
  return {
    ...config,
    params,
    headers
  };
};

export default httpClient;